import { z } from 'zod';
import { TASK_LIKE_ITEM_TYPES } from '@/lib/task-like-list-items';

export const NewItemInFolder = z.object({
  itemType: z.enum(['folder', 'file', 'text', 'task', 'attachment', 'media'], {
    required_error: 'Item type is required',
    invalid_type_error: 'Item type must be a string',
  }),
  index: z.number({
    required_error: 'Item position is required',
    invalid_type_error: 'Item position must be a number',
  }),
  parentId: z.number({
    required_error: 'Parent folder ID is required',
    invalid_type_error: 'Parent folder ID must be a number',
  }),
  groupId: z.number({
    required_error: 'Group ID is required',
    invalid_type_error: 'Group ID must be a number',
  }),
});

export const NewFolderItemSchema = NewItemInFolder.extend({
  name: z
    .string({
      required_error: 'Name is required',
      invalid_type_error: 'Name must be a string',
    })
    .trim()
    .min(1, {
      message: 'Name is required',
    }),
});

export const NewTextItemSchema = NewItemInFolder.extend({
  title: z
    .string({
      required_error: 'Title is required',
      invalid_type_error: 'Title must be a string',
    })
    .trim()
    .min(1, {
      message: 'Title is required',
    }),
});
const NewTaskItemSchema = NewItemInFolder.extend({
    title: z
      .string({
        required_error: 'Title is required',
        invalid_type_error: 'Title must be a string',
      })
      .trim()
      .min(1, {
        message: 'Title is required',
      }),
    dueDate: z
      .string({
        required_error: 'Due date is required',
        invalid_type_error: 'Due date must be a string',
      })
      .trim()
      .min(1, {
      message: 'Due date is required',
    }).optional(),
});


export const UpdateItemInFolderSchema = z.object({
  childItemType: z.enum(
    ['folder', 'file', 'text', 'task', 'attachment', 'media', 'ai_task', 'epic_task', 'ai_transcode_task', 'orchestrator_task'],
    {
    required_error: 'Item type is required',
    invalid_type_error: 'Item type must be a string',
    }
  ),
  parentId: z.union([z.number(), z.string()], {
    required_error: 'Parent folder ID is required',
    invalid_type_error: 'Parent folder ID must be valid',
  }),
  childId: z.union([z.number(), z.string()], {
    required_error: 'Child item ID is required',
    invalid_type_error: 'Child item ID must be valid',
  }),
  index: z.number({
    required_error: 'Item position is required',
    invalid_type_error: 'Item position must be a number',
  }),
  groupId: z.string({
    required_error: 'Group ID is required',
    invalid_type_error: 'Group ID must be a string',
  }),
});

export const UpdateFolderItemSchema = UpdateItemInFolderSchema.extend({
  name: z
    .string({
      required_error: 'Name is required',
      invalid_type_error: 'Name must be a string',
    })
    .trim()
    .min(1, {
      message: 'Name is required',
    }),
});

export const UpdateTextItemSchema = UpdateItemInFolderSchema.extend({
  title: z
    .string({
      required_error: 'Title is required',
      invalid_type_error: 'Title must be a string',
    })
    .trim()
    .min(1, {
      message: 'Title is required',
    }),
});

export const UpdateTaskItemSchema = UpdateItemInFolderSchema.extend({
  title: z
    .string({
      required_error: 'Title is required',
      invalid_type_error: 'Title must be a string',
    })
    .trim()
    .min(1, {
      message: 'Title is required',
    }).max(255, {
      message: 'Title must be less than 255 characters',
    }).optional(),
  dueDate: z
    .string({
      required_error: 'Due date is required',
      invalid_type_error: 'Due date must be a string',
    }).min(1, {
      message: 'Due date is required',
    }).trim().pipe(z.string())
    .optional(),
  index: z.number({
    required_error: 'Index is required',
    invalid_type_error: 'Index must be a number',
  }).optional(),
  parentId: z.string({
    required_error: 'Parent ID is required',
    invalid_type_error: 'Parent ID must be a string',
  }),
  itemType: z.string({
    required_error: 'Item type is required',
    invalid_type_error: 'Item type must be a string',
  }).default('task'),
}).omit({
  childItemType: true,
  childId: true,
});

export const UpdateItemSchemas = {
  folder: UpdateFolderItemSchema,
  text: UpdateTextItemSchema,
  attachment: UpdateItemInFolderSchema,
  media: UpdateItemInFolderSchema,
  ai_task: UpdateTextItemSchema,
  epic_task: UpdateTextItemSchema,
  ai_transcode_task: UpdateTextItemSchema,
  orchestrator_task: UpdateTextItemSchema,
} as const;

// Update Task Status
// Path: /api/v1/list-item/update-task-status/{taskItemId}
// Body: { status: 'PENDING'|'IN_PROGRESS'|'COMPLETED'|'FAILED'|'CHAT', groupId: number|string, itemType: 'TASK'|'AI_TASK'|'AI_TRANSCODE_TASK'|'EPIC_TASK'|'ORCHESTRATOR_TASK' }
export const UpdateTaskStatusSchema = z.object({
  status: z.enum(['PENDING', 'IN_PROGRESS', 'COMPLETED', 'FAILED', 'CHAT'], {
    required_error: 'Status is required',
    invalid_type_error: 'Status must be valid',
  }),
  groupId: z.union([z.string(), z.number()], {
    required_error: 'Group ID is required',
    invalid_type_error: 'Group ID must be valid',
  }).transform((v) => String(v)),
  itemType: z.enum(TASK_LIKE_ITEM_TYPES, {
    required_error: 'Item type is required',
    invalid_type_error: 'Item type must be valid',
  }),
});

// Update a cell of a folder's results table
// Path: PATCH /api/v1/folders/{folderId}/results/{recordId}/cell
// Body: { groupId, expectedRecordVersion: the row's `recordVersion` as last loaded, columnKey, newValue }
export const UpdateFolderResultCellSchema = z.object({
  groupId: z.union([z.string(), z.number()], {
    required_error: 'Group ID is required',
    invalid_type_error: 'Group ID must be valid',
  }).transform((v) => String(v)),
  expectedRecordVersion: z
    .number({
      required_error: 'Record version is required',
      invalid_type_error: 'Record version must be a number',
    })
    .int('Record version must be a whole number'),
  columnKey: z
    .string({
      required_error: 'Column key is required',
      invalid_type_error: 'Column key must be a string',
    })
    .min(1, 'Column key is required'),
  newValue: z.union([z.string(), z.number(), z.boolean(), z.null()], {
    required_error: 'New value is required',
    invalid_type_error: 'New value must be valid',
  }),
});

export const NewAttachmentItemSchema = NewItemInFolder.extend({
  resourceIds: z.array(z.number({
    required_error: 'Resource ID is required',
    invalid_type_error: 'Resource ID must be valid'
  })),
});

export const NewMediaItemSchema = NewAttachmentItemSchema;

export const NewAITaskItemSchema = NewItemInFolder.extend({
  prompt: z.string({
    required_error: 'Prompt is required',
    invalid_type_error: 'Prompt must be a string',
  }).optional(),
  itemType: z.string().default('AI_TASK'),
  title: z.string({
    required_error: 'Title is required',
    invalid_type_error: 'Title must be a string',
  }),
});

// Add List Item - EPIC Task 
export const NewEpicTaskItemSchema = NewItemInFolder.extend({
  itemType: z.string().default('EPIC_TASK'),
  title: z
    .string({ required_error: 'Title is required', invalid_type_error: 'Title must be a string' })
    .trim()
    .min(1, { message: 'Title is required' }),
});

// Add List Item - AI Transcode Task 
export const NewAITranscodeTaskItemSchema = NewItemInFolder.extend({
  itemType: z.literal('AI_TRANSCODE_TASK'),
  title: z
    .string({ required_error: 'Title is required', invalid_type_error: 'Title must be a string' })
    .trim()
    .min(1, { message: 'Title is required' }),
  transcodeSourceResourceId: z.number({
    required_error: 'transcodeSourceResourceId is required',
    invalid_type_error: 'transcodeSourceResourceId must be a number',
  }),
});

export const NewItemSchemas = {
  folder: NewFolderItemSchema,
  text: NewTextItemSchema,
  task: NewTaskItemSchema,
  attachment: NewAttachmentItemSchema,
  media: NewMediaItemSchema,
  ai_task: NewAITaskItemSchema,
  epic_task: NewEpicTaskItemSchema,
  ai_transcode_task: NewAITranscodeTaskItemSchema,
} as const;


export const UpdateParentChildItemSchema = z.object({
  parentId: z
    .union([z.number(), z.string()], {
      required_error: 'Parent ID is required',
      invalid_type_error: 'Parent ID must be valid',
    })
    .optional(), // Optional for home folder
  // childId: z.union([z.number(), z.string()], {
  //   required_error: 'Child ID is required',
  //   invalid_type_error: 'Child ID must be valid'
  // }).optional(),
  itemType: z
    .string({
      required_error: 'Item type is required',
      invalid_type_error: 'Item type must be valid',
    })
    .transform((val) => val.toUpperCase()),
  groupId: z.string({
    required_error: 'Group ID is required',
    invalid_type_error: 'Group ID must be valid',
  }),
  title: z.string().optional(), // Optional for folder and heading
  name: z.string().optional(), // Optional for folder
  status: z.enum(['OPEN', 'DONE']).optional(), // Optional for task
  reportedUserId: z.string().optional(), // Optional for task
  assignedUserId: z.string().optional(), // Optional for task
  repeatOn: z.string().optional(), // Optional for task
  dueDate: z.string().optional(), // Optional for task
  childItems: z
    .array(
      z.object({
        childId: z.union([z.number(), z.string()], {
          required_error: 'Child ID is required',
          invalid_type_error: 'Child ID must be valid',
        }),
        itemType: z.string({
            required_error: 'Child item type is required',
            invalid_type_error: 'Child item type must be valid',
          })
          .transform(val => val.toUpperCase()),
        index: z.union([z.number(), z.string()], {
          required_error: 'Index is required',
          invalid_type_error: 'Index must be valid',
        }),
        title: z.string().optional(), // Optional for heading and text
        name: z.string().optional(), // Optional for folder
      })
    )
    .optional(), // Optional for parent item
  prompt: z.string().optional(),
});

export type UpdateParentChildItemType = z.infer<typeof UpdateParentChildItemSchema>;

export type UpdateFolderResultCellRequest = z.input<typeof UpdateFolderResultCellSchema>;
